/**
 * Qualidade (tamanho da imagem) e orçamentos de memória do loop, no modelo de
 * cache em dois níveis (ver frameCache.ts):
 *
 * - orçamento COMPRIMIDO (bytes de JPEG baixados): limita quantos quadros o
 *   loop pode ter em cada tamanho — só quando estoura mostramos "os mais
 *   recentes". Com os arquivos no DISCO (frameStorage.ts), é o espaço que o
 *   navegador concede ao site; `budgetBytes` abaixo é o da RAM, usado quando
 *   não há disco;
 * - orçamento DECODIFICADO (pixels, sempre RAM): limita só a JANELA de
 *   quadros prontos para desenhar (os próximos na reprodução; de preferência o
 *   loop inteiro, ver `loopDecodeWidth`).
 */
import { imagePixels, type ImageWidth } from './star';

export type QualityMode = 'auto' | 900 | 1800 | 3600 | 7200;

export const QUALITY_MODES: QualityMode[] = ['auto', 900, 1800, 3600, 7200];

export const QUALITY_LABELS: Record<QualityMode, string> = {
  auto: 'Automática',
  900: 'Leve',
  1800: 'Padrão',
  3600: 'Alta',
  7200: 'Máxima',
};

export function isQualityMode(v: unknown): v is QualityMode {
  return v === 'auto' || v === 900 || v === 1800 || v === 3600 || v === 7200;
}

/** Menor largura usada em loops. */
export const MIN_LOOP_WIDTH: ImageWidth = 900;

/**
 * Largura "útil" para o zoom: pixels do setor na tela × densidade (até 2),
 * aceitando ampliar até 15%.
 */
export const USEFUL_UPSCALE = 1.15;
/**
 * Histerese para SUBIR de largura na automática: o zoom precisa passar 10% do
 * limite (evita trocar por um toque no zoom). Descer nunca desce.
 */
export const UP_HYSTERESIS = 1.1;

/**
 * Janela decodificada: de 2 quadros até o loop mais longo que a interface
 * oferece (48 h a cada 10 min). O teto antigo de 36 fazia a janela girar em
 * loops maiores mesmo quando havia memória para o loop inteiro — e cada volta
 * da janela é uma redecodificação por quadro, o que pesa no processador.
 */
export const MIN_DECODE_WINDOW = 2;
export const MAX_DECODE_WINDOW = 288;

export interface DeviceProfile {
  /** Orçamento de pixels DECODIFICADOS (janela de reprodução, todas as camadas). */
  budgetPixels: number;
  /**
   * Orçamento de bytes COMPRIMIDOS baixados (loop inteiro, todas as camadas)
   * quando ficam na RAM. Com disco vale o do disco (`compressedBudget`).
   */
  budgetBytes: number;
  /** Maior largura que a qualidade automática usa em loops. */
  maxAutoLoopWidth: ImageWidth;
  /** Permite trocar o quadro parado por uma versão mais nítida. */
  allowStillUpgrade: boolean;
  /** Maior largura do quadro parado mais nítido. */
  maxStillWidth: ImageWidth;
  /** Densidade de tela considerada (qualidade automática e quadro parado). */
  stillMaxDpr: number;
  dpr: number;
  mobile: boolean;
}

interface NavigatorExtras {
  deviceMemory?: number;
  maxTouchPoints?: number;
  connection?: { saveData?: boolean; effectiveType?: string };
}

/** O que sabemos do aparelho (lido do navegador por `readDeviceEnv`). */
export interface DeviceEnv {
  dpr: number;
  /** Ponteiro principal grosso (dedo). */
  coarse: boolean;
  /** Menor dimensão da tela (px CSS). */
  screenMin: number;
  maxTouchPoints: number;
  /** GB (só Chrome/Edge informam; indefinido nos demais). */
  deviceMemory?: number;
  saveData: boolean;
}

export function readDeviceEnv(): DeviceEnv {
  const nav = (typeof navigator !== 'undefined' ? navigator : {}) as NavigatorExtras;
  const hasWindow = typeof window !== 'undefined';
  return {
    dpr: hasWindow ? window.devicePixelRatio || 1 : 1,
    coarse: hasWindow ? (window.matchMedia?.('(pointer: coarse)').matches ?? false) : false,
    screenMin: hasWindow ? Math.min(window.screen?.width ?? 1024, window.screen?.height ?? 1024) : 1024,
    maxTouchPoints: nav.maxTouchPoints ?? 0,
    deviceMemory: nav.deviceMemory,
    saveData: !!nav.connection?.saveData || /(^|-)2g$|^3g$/.test(nav.connection?.effectiveType ?? ''),
  };
}

/**
 * Perfil do aparelho (a coluna "comprimido" é a RAM, usada só sem disco):
 *
 * | aparelho                               | comprimido | decodificado | loop auto | parado |
 * |----------------------------------------|-----------:|-------------:|----------:|-------:|
 * | celular (toque + tela < 768 px)        |     200 MB |       100 MP |   3600 px | 3600   |
 * | tablet / tela de toque (ex.: iPad)     |     400 MB |       100 MP |   3600 px | 3600   |
 * | desktop, memória ≥ 8 GB                |     1,5 GB |       300 MP |   7200 px | 7200   |
 * | desktop, 4–8 GB ou desconhecida*       |     800 MB |   200/150 MP |   7200 px | 7200   |
 * | desktop, < 4 GB                        |     400 MB |       120 MP |   7200 px | 7200   |
 *
 * A automática começa pela largura que o zoom pede (900/1800 no celular) e
 * só chega às maiores se o orçamento comprimido comportar os quadros.
 *
 * (*) Safari e Firefox não informam `deviceMemory`.
 * "Economia de dados": loop até 900 px e sem quadro parado nítido.
 */
export function profileFor(env: DeviceEnv): DeviceProfile {
  const phone = env.coarse && env.screenMin < 768;
  const touch = phone || env.maxTouchPoints > 1;
  const mem = env.deviceMemory;

  let budgetPixels: number;
  let budgetBytes: number;
  if (phone) {
    budgetPixels = 100e6;
    budgetBytes = 200e6;
  } else if (touch) {
    budgetPixels = 100e6;
    budgetBytes = 400e6;
  } else if (mem === undefined) {
    budgetPixels = 150e6;
    budgetBytes = 800e6;
  } else if (mem < 4) {
    budgetPixels = 120e6;
    budgetBytes = 400e6;
  } else if (mem < 8) {
    budgetPixels = 200e6;
    budgetBytes = 800e6;
  } else {
    budgetPixels = 300e6;
    budgetBytes = 1.5e9;
  }

  return {
    budgetPixels,
    budgetBytes,
    maxAutoLoopWidth: env.saveData ? 900 : touch ? 3600 : 7200,
    allowStillUpgrade: !env.saveData,
    maxStillWidth: touch ? 3600 : 7200,
    stillMaxDpr: touch ? 1.5 : 2,
    dpr: env.dpr,
    mobile: phone,
  };
}

/** Detecta o perfil do aparelho (memória, tela, toque, economia de dados). */
export function detectDeviceProfile(): DeviceProfile {
  return profileFor(readDeviceEnv());
}

/**
 * Quantos pixels de tela (físicos) o setor inteiro ocupa na resolução atual do
 * mapa. `sectorWidth` em unidades do mapa; `resolution` em unidades/pixel CSS.
 */
export function sectorScreenPixels(sectorWidth: number, resolution: number, dpr: number): number {
  return (sectorWidth / resolution) * dpr;
}

/** Menor largura publicada ≥ `px`, limitada a [min, max]. */
export function smallestWidthAtLeast(px: number, min: ImageWidth, max: ImageWidth): ImageWidth {
  const widths: ImageWidth[] = [450, 900, 1800, 3600, 7200];
  for (const w of widths) {
    if (w < min) continue;
    if (w >= px || w >= max) return Math.min(w, max) as ImageWidth;
  }
  return max;
}

/** Largura útil para o zoom atual (sem limite de orçamento). */
export function usefulWidth(sectorCssPixels: number | null, profile: DeviceProfile, max: ImageWidth): ImageWidth {
  const css = sectorCssPixels ?? 1800;
  return smallestWidthAtLeast((css * Math.min(profile.dpr, profile.stillMaxDpr)) / USEFUL_UPSCALE, MIN_LOOP_WIDTH, max);
}

/**
 * Tamanho da janela decodificada: orçamento de pixels ÷ (pixels por quadro ×
 * camadas), entre `MIN_DECODE_WINDOW` e `MAX_DECODE_WINDOW`.
 */
export function decodeWindowSize(
  budgetPixels: number,
  width: ImageWidth,
  layerCount: number,
  decodeWidth: number | null = null,
): number {
  const perFrame = decodeWidth ? decodedPixels(decodeWidth) : imagePixels(width);
  const n = Math.floor(budgetPixels / (perFrame * Math.max(1, layerCount)));
  return Math.max(MIN_DECODE_WINDOW, Math.min(MAX_DECODE_WINDOW, n));
}

/** Pixels de um bitmap decodificado com esta largura (proporção 5:3 das imagens). */
export function decodedPixels(decodeWidth: number): number {
  return decodeWidth * Math.round((decodeWidth * 3) / 5);
}

/**
 * Degraus em que um arquivo grande é DECODIFICADO quando a tela não precisa
 * dele inteiro (ver `decodeWidthFor`). Alturas inteiras (3/5 da largura).
 */
export const DECODE_STEPS: readonly number[] = [1800, 2400, 3000, 3600, 4200, 4800, 5400, 6000, 6600];

/**
 * Degraus válidos para um arquivo. No de 3600 px só os que ao menos dividem
 * os pixels por dois (≤ 70% da largura): abaixo disso a reamostragem no
 * worker custa mais do que economiza. O de 7200 usa todos — são 31 MP (124 MB
 * decodificados) por quadro.
 */
export function decodeSteps(fileWidth: ImageWidth): readonly number[] {
  if (fileWidth >= 7200) return DECODE_STEPS;
  if (fileWidth >= 3600) return DECODE_STEPS.filter((s) => s <= fileWidth * 0.7);
  return [];
}

/**
 * Largura em que decodificar um arquivo (bitmap) para a ANIMAÇÃO, ou null =
 * tamanho natural.
 *
 * O arquivo é o que o botão pediu (na qualidade fixa) ou o que o zoom pede
 * (na automática), mas a TELA só mostra os pixels que tem: com "Máxima" e o
 * mapa afastado, um quadro de 7200 px (124 MB decodificado) vira ~2400 px na
 * tela. Decodificar direto no degrau que a tela usa — o menor ≥ pixels do
 * setor na tela ÷ 1,15, a MESMA tolerância de ampliação usada para escolher o
 * arquivo (`usefulWidth`) — dá a mesma imagem na tela com até 9× menos
 * memória: cabem mais quadros prontos (menos redecodificação) e cada quadro
 * novo custa muito menos para chegar à GPU, que era o que travava a interface.
 *
 * O arquivo baixado continua inteiro: ao aproximar o zoom o degrau sobe
 * (redecodificação da janela) até o tamanho natural; descer só depois de a
 * vista ficar parada; e com a animação PARADA o quadro atual volta ao tamanho
 * natural (ver preloader.ts) — quem olha de perto vê o arquivo inteiro.
 */
export function decodeWidthFor(fileWidth: ImageWidth, sectorCssPixels: number | null, profile: DeviceProfile): number | null {
  const steps = decodeSteps(fileWidth);
  if (steps.length === 0 || sectorCssPixels === null) return null;
  const need = (sectorCssPixels * Math.min(profile.dpr, profile.stillMaxDpr)) / USEFUL_UPSCALE;
  for (const step of steps) if (step >= need) return step;
  return null;
}

/**
 * Quadros que passam pela decodificação a cada volta do loop, como fração do
 * loop (0 = o loop inteiro cabe). Ver `decodeWindow` em loop/frames.ts: a
 * janela deixa `slots − STREAM_SLOTS` quadros fixos e passa os outros.
 */
export function streamedFraction(frameCount: number, slots: number): number {
  if (frameCount <= 0 || slots >= frameCount) return 0;
  const resident = Math.max(0, slots - STREAM_SLOTS_BUDGET);
  return (frameCount - resident) / frameCount;
}
/** Igual a `STREAM_SLOTS` (loop/frames.ts), repetido para budget.ts não depender do loop. */
const STREAM_SLOTS_BUDGET = 2;

/**
 * Até esta fração do loop pode passar pela decodificação a cada volta. É o
 * equilíbrio entre nitidez e processador: a 5 q/s, um quarto do loop dá
 * ~1 decodificação por segundo (~0,3 s de processador num worker), contra 5
 * por segundo da janela que deslizava.
 */
export const MAX_STREAMED_FRACTION = 0.25;

/**
 * Largura de decodificação da ANIMAÇÃO.
 *
 * Parte do degrau que a tela pede (`decodeWidthFor`) e desce só o necessário
 * para que no máximo `MAX_STREAMED_FRACTION` do loop precise ser decodificado
 * de novo a cada volta. Antes a regra era o loop INTEIRO caber: com 6 h, 12 h,
 * tela grande ou o radar ligado, o degrau caía para 25–50% do que a tela
 * pedia. Agora os quadros que não cabem passam por dois lugares rotativos
 * (`decodeWindow`), e o resto fica fixo: um pouco de processador compra um ou
 * dois degraus de nitidez. Com a animação parada o quadro atual volta ao
 * tamanho natural (preloader.ts).
 *
 * Se nem o menor degrau fica abaixo da fração (48 h no celular), o menor
 * degrau — o que menos pesa.
 *
 * Sem vista conhecida (`sectorCssPixels` null) ou sem degraus para o arquivo:
 * tamanho natural, como `decodeWidthFor`.
 */
export function loopDecodeWidth(
  fileWidth: ImageWidth,
  sectorCssPixels: number | null,
  profile: DeviceProfile,
  frameCount: number,
  layerCount: number,
  budgetPixels: number,
): number | null {
  const wanted = decodeWidthFor(fileWidth, sectorCssPixels, profile);
  const steps = decodeSteps(fileWidth);
  if (steps.length === 0 || sectorCssPixels === null) return wanted;
  const cheap = (w: number | null) =>
    streamedFraction(frameCount, decodeWindowSize(budgetPixels, fileWidth, layerCount, w)) <= MAX_STREAMED_FRACTION;
  if (cheap(wanted)) return wanted;
  const candidates = steps.filter((s) => wanted === null || s < wanted).sort((a, b) => b - a);
  for (const s of candidates) if (cheap(s)) return s;
  return candidates.at(-1) ?? wanted;
}

export interface LoopWidthInput {
  mode: QualityMode;
  /** Largura (px CSS) do setor na tela — `sectorWidth / resolution`. Null se desconhecida. */
  sectorCssPixels: number | null;
  frameCount: number;
  profile: DeviceProfile;
  /**
   * Bytes comprimidos do loop inteiro (todas as camadas) em cada largura —
   * calculados com os tamanhos reais da listagem.
   */
  bytesFor: (width: ImageWidth) => number;
  /** Maior largura automática já usada na sessão: a automática só sobe. */
  autoFloor?: ImageWidth | null;
}

export interface LoopWidthChoice {
  width: ImageWidth;
  /** Quadros que cabem no orçamento comprimido nessa largura. */
  capacity: number;
}

/** Quantos quadros cabem no orçamento comprimido. */
export function compressedCapacity(width: ImageWidth, frameCount: number, bytesFor: (w: ImageWidth) => number, budgetBytes: number): number {
  const total = bytesFor(width);
  if (frameCount <= 0 || total <= 0) return Math.max(1, frameCount);
  const perFrame = total / frameCount;
  return Math.max(1, Math.floor(budgetBytes / perFrame));
}

/**
 * Largura dos quadros do loop.
 * - Fixa ("Alta", "Máxima"…): respeitada; se o período não couber no
 *   orçamento comprimido, ficam os quadros mais recentes (capacidade).
 * - Automática ("acompanha o zoom"): a largura útil para o zoom (sobe ao
 *   aproximar, com histerese; nunca desce — afastar não baixa nada de novo),
 *   limitada ao que o orçamento comprimido comporta para esse número de quadros.
 */
export function chooseLoopWidth(input: LoopWidthInput): LoopWidthChoice {
  const { mode, sectorCssPixels, frameCount, profile, bytesFor, autoFloor } = input;
  if (mode !== 'auto') {
    return { width: mode, capacity: compressedCapacity(mode, frameCount, bytesFor, profile.budgetBytes) };
  }
  let width = usefulWidth(sectorCssPixels, profile, profile.maxAutoLoopWidth);
  if (autoFloor && autoFloor <= profile.maxAutoLoopWidth) {
    // Já há uma largura na sessão: sobe só com folga (histerese) e nunca desce.
    const css = sectorCssPixels ?? 1800;
    const withMargin = smallestWidthAtLeast(
      (css * Math.min(profile.dpr, profile.stillMaxDpr)) / (USEFUL_UPSCALE * UP_HYSTERESIS),
      MIN_LOOP_WIDTH,
      profile.maxAutoLoopWidth,
    );
    width = Math.max(autoFloor, withMargin) as ImageWidth;
  }
  while (width > MIN_LOOP_WIDTH && bytesFor(width) > profile.budgetBytes) {
    width = (width / 2) as ImageWidth;
  }
  return { width, capacity: compressedCapacity(width, frameCount, bytesFor, profile.budgetBytes) };
}

/**
 * Com o loop parado na qualidade automática, largura mais nítida para o quadro
 * atual (ou null se a do loop já basta).
 */
export function chooseStillWidth(
  mode: QualityMode,
  sectorCssPixels: number | null,
  loopWidth: ImageWidth,
  profile: DeviceProfile,
): ImageWidth | null {
  if (mode !== 'auto' || !profile.allowStillUpgrade || sectorCssPixels === null) return null;
  const width = usefulWidth(sectorCssPixels, profile, profile.maxStillWidth);
  return width > loopWidth ? width : null;
}
